// Shared level data — imported by both server (authoritative physics/collision)
// and client (renderer). World-space coordinates, origin top-left.
//
// The game is a 10-level campaign; every level is a themed dash along a stretch
// of Visakhapatnam coastline/hills. Layouts here are procedurally generated from
// a per-level seed + tuning knobs (see `generateLayout`) so all ten are
// immediately playable and correctly sized (~3+ minutes of traversal each);
// hand-authoring passes are tracked in docs/levels/*.md.

export type Platform = { x: number; y: number; width: number; height: number };

// A pit in the ground strip — no ground collision inside [x, x+width), so a
// player who doesn't jump clean over it falls through to fall-death/respawn
// (see footprintClearOfGaps in server/physics.ts).
export type GroundGap = { x: number; width: number };

export type EnemySpawn = {
	id: string;
	type: "crab" | "seagull";
	x: number;
	y: number;
	patrolMinX: number;
	patrolMaxX: number;
	speed: number; // px/sec
};

export type Collectible = {
	id: string;
	type: "punugulu" | "corn" | "coconut";
	x: number;
	y: number;
	value: number;
};

export type PowerUpSpawn = { id: string; x: number; y: number };

// A Visakhapatnam landmark rendered as a non-interactive background set-piece
// (silhouette + name tag) at world-x `x`. `kind` picks the silhouette shape.
export type LandmarkKind =
	| "hill"
	| "lighthouse"
	| "temple"
	| "submarine"
	| "statue"
	| "dome"
	| "pier"
	| "caves"
	| "ferris"
	| "bridge"
	| "aircraft"
	| "port";

export type Landmark = { name: string; x: number; kind: LandmarkKind; scale?: number };

// Per-level mood grading. The seaside art stays the same PNG set across all ten
// levels (see image-sprites.ts); the theme shifts palette / time-of-day.
export type LevelTheme = {
	skyTop: string;
	skyBottom: string;
	tint: string; // wash drawn over the whole scene
	tintAlpha: number; // 0..1
	waterTint: string; // multiplied over the ocean band
	sandTint: string; // multiplied over the ground/platform tiles
	cloudAlpha: number;
	label: string; // e.g. "Dawn", "Golden hour"
};

export type LevelConfig = {
	id: number;
	slug: string;
	name: string;
	blurb: string;
	width: number;
	height: number;
	groundY: number;
	spawnX: number;
	checkpointX: number;
	finishX: number;
	platforms: Platform[];
	enemies: EnemySpawn[];
	collectibles: Collectible[];
	powerUp: PowerUpSpawn;
	landmarks: Landmark[];
	theme: LevelTheme;
	groundGaps: GroundGap[];
};

export const WORLD_HEIGHT = 480;
export const GROUND_Y = 440; // top surface of the ground strip

export const PLAYER_WIDTH = 24;
export const PLAYER_HEIGHT = 32;
export const ENEMY_WIDTH = 24;
export const ENEMY_HEIGHT = 20;

// ---------------------------------------------------------------------------
// Procedural layout generator
// ---------------------------------------------------------------------------

// Small deterministic PRNG so a given (seed) always yields the same level.
function mulberry32(seed: number): () => number {
	let a = seed >>> 0;
	return () => {
		a |= 0;
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

type LayoutKnobs = {
	seed: number;
	width: number;
	groundY: number;
	/** difficulty 0..1 — scales enemy density and speed. */
	difficulty: number;
};

const COLLECTIBLE_TYPES: Collectible["type"][] = ["punugulu", "corn", "coconut"];

// Jump envelope — derived from server/physics.ts (GRAVITY 1400, JUMP_VELOCITY
// -520, MOVE_SPEED 180). Keep these in sync with that module; the
// jump-reachability test asserts they match.
//   apex height  = JUMP_VELOCITY^2 / (2*GRAVITY)          ≈ 96.6 px
//   full airtime = 2 * |JUMP_VELOCITY| / GRAVITY          ≈ 0.743 s
//   horiz range  = MOVE_SPEED * full airtime              ≈ 133.7 px
export const MAX_JUMP_HEIGHT = 96;
export const MAX_JUMP_RUN = 133;
// What the generator actually builds to — a margin under the theoretical apex
// so discrete-tick integration and a non-zero horizontal speed still clear it.
export const SAFE_JUMP_UP = 82;

// Is platform `p` reachable by a jump from the ground or from an earlier
// (lower, horizontally near) platform? A launch surface qualifies when it sits
// below the platform, no more than SAFE_JUMP_UP px above its top, and within a
// full jump run of its span.
function platformReachable(
	p: Platform,
	groundY: number,
	surfaces: readonly { minX: number; maxX: number; y: number }[],
): boolean {
	let bestLaunchY = groundY; // the ground spans the whole world
	for (const s of surfaces) {
		if (s.y <= p.y) continue; // not below the platform — can't launch upward from it
		const gap = Math.max(s.minX - (p.x + p.width), p.x - s.maxX, 0);
		if (gap > MAX_JUMP_RUN) continue;
		if (s.y < bestLaunchY) bestLaunchY = s.y;
	}
	return p.y >= bestLaunchY - SAFE_JUMP_UP;
}

// Lower (increase y of) any platform the jump arc can't actually reach, so
// every bonus route is physically completable. Coins are re-seated onto their
// platform afterwards by the caller.
export function enforceReachablePlatforms(platforms: Platform[], groundY: number): void {
	const surfaces: { minX: number; maxX: number; y: number }[] = [];
	for (const p of platforms) {
		if (!platformReachable(p, groundY, surfaces)) {
			// Raise the platform toward the best available launch surface.
			let bestLaunchY = groundY;
			for (const s of surfaces) {
				if (s.y <= p.y) continue;
				const gap = Math.max(s.minX - (p.x + p.width), p.x - s.maxX, 0);
				if (gap > MAX_JUMP_RUN) continue;
				if (s.y < bestLaunchY) bestLaunchY = s.y;
			}
			p.y = bestLaunchY - SAFE_JUMP_UP;
		}
		surfaces.push({ minX: p.x, maxX: p.x + p.width, y: p.y });
	}
}

function generateLayout(k: LayoutKnobs): Pick<
	LevelConfig,
	"platforms" | "enemies" | "collectibles" | "powerUp" | "spawnX" | "checkpointX" | "finishX" | "groundGaps"
> {
	const rand = mulberry32(k.seed);
	const rangeInt = (min: number, max: number) => Math.floor(min + rand() * (max - min + 1));

	const spawnX = 40;
	const finishX = k.width - 220;
	const checkpointX = Math.round(k.width * 0.5);

	const platforms: Platform[] = [];
	const collectibles: Collectible[] = [];
	const enemies: EnemySpawn[] = [];

	// Platforms — jumpable bonus routes above the always-present ground line.
	// The first step of a cluster is jumped from the ground, so it must stay
	// within SAFE_JUMP_UP; later steps climb by at most SAFE_JUMP_UP from the
	// platform directly below. enforceReachablePlatforms() below is the backstop.
	type PlatformCoin = { platformIndex: number; collectibleIndex: number };
	const platformCoins: PlatformCoin[] = [];
	let px = 460;
	let pIndex = 0;
	while (px < finishX - 360) {
		const stairs = rand() < 0.28 ? rangeInt(2, 3) : 1;
		let stepY = k.groundY - rangeInt(48, SAFE_JUMP_UP);
		for (let s = 0; s < stairs && px < finishX - 300; s++) {
			const w = rangeInt(70, 150);
			const platformIndex = platforms.length;
			platforms.push({ x: px, y: stepY, width: w, height: 16 });
			// A coin sits on roughly every other platform.
			if (pIndex % 2 === 0) {
				platformCoins.push({ platformIndex, collectibleIndex: collectibles.length });
				collectibles.push({
					id: `c-${k.seed}-p${pIndex}`,
					type: COLLECTIBLE_TYPES[pIndex % COLLECTIBLE_TYPES.length],
					x: px + w / 2 - 6,
					y: stepY - 26,
					value: 10,
				});
			}
			px += w + rangeInt(40, 90);
			// Climb by at most SAFE_JUMP_UP so each step is reachable from the one below.
			stepY = Math.max(k.groundY - 200, stepY - rangeInt(24, 52));
			pIndex++;
		}
		px += rangeInt(180, 380); // running stretch between platform clusters
	}

	// Backstop: nudge down anything the jump arc still can't reach, then re-seat
	// each affected coin onto its (possibly lowered) platform.
	enforceReachablePlatforms(platforms, k.groundY);
	for (const { platformIndex, collectibleIndex } of platformCoins) {
		collectibles[collectibleIndex].y = platforms[platformIndex].y - 26;
	}

	// Ground-level collectibles fill the running stretches.
	for (let cx = 300, i = 0; cx < finishX - 200; cx += rangeInt(360, 640), i++) {
		collectibles.push({
			id: `c-${k.seed}-g${i}`,
			type: COLLECTIBLE_TYPES[i % COLLECTIBLE_TYPES.length],
			x: cx,
			y: k.groundY - 22,
			value: 10,
		});
	}

	// Enemies — crabs patrol the ground, seagulls bob in the air. Density and
	// speed both climb with difficulty.
	const enemyGap = Math.round(920 - k.difficulty * 380); // ~920 → ~540 px
	let crabN = 0;
	let gullN = 0;
	for (let ex = 360; ex < finishX - 200; ex += enemyGap + rangeInt(-120, 160)) {
		if (rand() < 0.62) {
			const span = rangeInt(90, 190);
			const speed = Math.round(38 + k.difficulty * 44 + rand() * 16);
			enemies.push({
				id: `crab-${k.seed}-${crabN++}`,
				type: "crab",
				x: ex,
				y: k.groundY - ENEMY_HEIGHT,
				patrolMinX: ex - span,
				patrolMaxX: ex + span,
				speed,
			});
		} else {
			const span = rangeInt(120, 240);
			const speed = Math.round(52 + k.difficulty * 46 + rand() * 20);
			const y = k.groundY - rangeInt(120, 220);
			enemies.push({
				id: `seagull-${k.seed}-${gullN++}`,
				type: "seagull",
				x: ex,
				y,
				patrolMinX: ex - span,
				patrolMaxX: ex + span,
				speed,
			});
		}
	}

	const powerUp: PowerUpSpawn = {
		id: `power-${k.seed}`,
		x: Math.round(k.width * 0.58),
		y: k.groundY - 60,
	};

	// Ground pits — jumpable but punish a player who doesn't clear them (see
	// footprintClearOfGaps in server/physics.ts). Kept well under MAX_JUMP_RUN
	// so a running jump always clears one, and spaced out from spawn/finish.
	const groundGaps: GroundGap[] = [];
	for (let gx = 900; gx < finishX - 500; gx += rangeInt(1400, 2400)) {
		groundGaps.push({ x: gx, width: rangeInt(40, 90) });
	}

	return { platforms, enemies, collectibles, powerUp, spawnX, checkpointX, finishX, groundGaps };
}

// ---------------------------------------------------------------------------
// The ten-level campaign
// ---------------------------------------------------------------------------

type LevelSeed = {
	slug: string;
	name: string;
	blurb: string;
	width: number;
	difficulty: number;
	theme: LevelTheme;
	/** 3+ Visakhapatnam landmarks per level, spread across the world by spreadLandmarks(). */
	landmarks: [Omit<Landmark, "x">, Omit<Landmark, "x">, Omit<Landmark, "x">, ...Omit<Landmark, "x">[]];
};

const LEVEL_SEEDS: LevelSeed[] = [
	{
		slug: "rk-beach-dawn",
		name: "RK Beach at Dawn",
		blurb: "First light on Ramakrishna Beach — an easy warm-up jog down the sand.",
		width: 33500,
		difficulty: 0.1,
		theme: {
			skyTop: "#3b4a86",
			skyBottom: "#ffb9a0",
			tint: "#ff8fae",
			tintAlpha: 0.16,
			waterTint: "#ffd0c0",
			sandTint: "#ffe6c8",
			cloudAlpha: 0.75,
			label: "Dawn",
		},
		landmarks: [
			{ name: "Kali Temple", kind: "temple" },
			{ name: "Matsyadarshini Aquarium", kind: "dome" },
			{ name: "RK Beach Promenade", kind: "pier" },
		],
	},
	{
		slug: "beach-road-cruise",
		name: "Beach Road Cruise",
		blurb: "Midday sprint past the war memorials along Beach Road.",
		width: 34000,
		difficulty: 0.22,
		theme: {
			skyTop: "#1f6fd6",
			skyBottom: "#a9e4ff",
			tint: "#ffffff",
			tintAlpha: 0.04,
			waterTint: "#d8f4ff",
			sandTint: "#fff2d8",
			cloudAlpha: 1,
			label: "Bright noon",
		},
		landmarks: [
			{ name: "Kursura Submarine Museum", kind: "submarine" },
			{ name: "TU-142 Aircraft Museum", kind: "aircraft" },
			{ name: "VUDA Park", kind: "ferris" },
		],
	},
	{
		slug: "dolphins-nose",
		name: "Dolphin's Nose",
		blurb: "Haze over the headland — climb toward the old lighthouse.",
		width: 36000,
		difficulty: 0.34,
		theme: {
			skyTop: "#8fb8d8",
			skyBottom: "#e8ddc4",
			tint: "#c9c2a8",
			tintAlpha: 0.2,
			waterTint: "#bcd0d4",
			sandTint: "#efe2c4",
			cloudAlpha: 0.6,
			label: "Hazy midday",
		},
		landmarks: [
			{ name: "Dolphin's Nose Cliff", kind: "hill", scale: 1.3 },
			{ name: "Dolphin's Nose Lighthouse", kind: "lighthouse" },
			{ name: "Gangavaram Port", kind: "port" },
		],
	},
	{
		slug: "yarada-shores",
		name: "Yarada Shores",
		blurb: "Turquoise afternoon on the Blue Flag beach beyond the hills.",
		width: 37000,
		difficulty: 0.42,
		theme: {
			skyTop: "#159ac9",
			skyBottom: "#b7f2e8",
			tint: "#2ec6b0",
			tintAlpha: 0.12,
			waterTint: "#7ff0dc",
			sandTint: "#ffeccb",
			cloudAlpha: 0.9,
			label: "Afternoon",
		},
		landmarks: [
			{ name: "Yarada Beach", kind: "pier" },
			{ name: "Yarada Hills", kind: "hill" },
			{ name: "Yarada Boardwalk", kind: "bridge" },
		],
	},
	{
		slug: "kailasagiri-ropeway",
		name: "Kailasagiri Ropeway",
		blurb: "Golden hour on the hilltop — mind the gap under the cable car.",
		width: 39000,
		difficulty: 0.52,
		theme: {
			skyTop: "#e88a2f",
			skyBottom: "#ffe3a6",
			tint: "#ff9d3c",
			tintAlpha: 0.22,
			waterTint: "#ffcf9a",
			sandTint: "#ffdca6",
			cloudAlpha: 0.8,
			label: "Golden hour",
		},
		landmarks: [
			{ name: "Kailasagiri Hill", kind: "hill", scale: 1.4 },
			{ name: "Shiva–Parvati Statue", kind: "statue" },
			{ name: "Titanic View Point", kind: "pier" },
		],
	},
	{
		slug: "rushikonda-surf",
		name: "Rushikonda Surf",
		blurb: "Sunset breakers below the IT hills.",
		width: 40000,
		difficulty: 0.6,
		theme: {
			skyTop: "#c0491f",
			skyBottom: "#ffd07a",
			tint: "#ff6a3c",
			tintAlpha: 0.24,
			waterTint: "#ffb27a",
			sandTint: "#ffd2a0",
			cloudAlpha: 0.7,
			label: "Sunset",
		},
		landmarks: [
			{ name: "Rushikonda Beach", kind: "pier" },
			{ name: "Rushikonda IT Hills", kind: "hill" },
			{ name: "Thotlakonda Monastery", kind: "temple" },
		],
	},
	{
		slug: "simhachalam-climb",
		name: "Simhachalam Climb",
		blurb: "Dusk on the temple hill — a steep, enemy-thick ascent.",
		width: 41000,
		difficulty: 0.7,
		theme: {
			skyTop: "#4a2f7a",
			skyBottom: "#e79cc0",
			tint: "#7a3fa0",
			tintAlpha: 0.26,
			waterTint: "#b090c0",
			sandTint: "#e8c8b0",
			cloudAlpha: 0.6,
			label: "Dusk",
		},
		landmarks: [
			{ name: "Simhachalam Temple", kind: "temple", scale: 1.3 },
			{ name: "Kappasthambham Pillar", kind: "statue" },
			{ name: "Adivaram Gateway", kind: "dome" },
		],
	},
	{
		slug: "harbour-nights",
		name: "Harbour Nights",
		blurb: "Port lights on the water — fast and unforgiving.",
		width: 43000,
		difficulty: 0.8,
		theme: {
			skyTop: "#0b1636",
			skyBottom: "#243b6b",
			tint: "#12204a",
			tintAlpha: 0.42,
			waterTint: "#2a4a80",
			sandTint: "#8a7f6a",
			cloudAlpha: 0.35,
			label: "Night",
		},
		landmarks: [
			{ name: "Visakhapatnam Port", kind: "port", scale: 1.3 },
			{ name: "Fishing Harbour", kind: "submarine" },
			{ name: "RK Beach Lights", kind: "ferris" },
		],
	},
	{
		slug: "araku-monsoon",
		name: "Araku Monsoon",
		blurb: "Mist in the valley — slippery going through the coffee hills.",
		width: 44000,
		difficulty: 0.9,
		theme: {
			skyTop: "#6d7d78",
			skyBottom: "#c7d2c0",
			tint: "#8fa090",
			tintAlpha: 0.3,
			waterTint: "#9db0a6",
			sandTint: "#cfc8a8",
			cloudAlpha: 0.5,
			label: "Misty",
		},
		landmarks: [
			{ name: "Araku Valley", kind: "hill", scale: 1.5 },
			{ name: "Borra Caves", kind: "caves" },
			{ name: "Ananthagiri Coffee Hills", kind: "hill" },
		],
	},
	{
		slug: "kambalakonda-wild",
		name: "Kambalakonda Wild",
		blurb: "The green finale — a marathon through the sanctuary to the reservoir.",
		width: 46000,
		difficulty: 1,
		theme: {
			skyTop: "#1f5a3a",
			skyBottom: "#bfe08a",
			tint: "#2f7a48",
			tintAlpha: 0.24,
			waterTint: "#7fc890",
			sandTint: "#d8cf9a",
			cloudAlpha: 0.7,
			label: "Deep green",
		},
		landmarks: [
			{ name: "Kambalakonda Sanctuary", kind: "hill", scale: 1.4 },
			{ name: "Mudasarlova Reservoir", kind: "bridge" },
			{ name: "Indira Gandhi Zoological Park", kind: "dome" },
		],
	},
];

function spreadLandmarks(base: Omit<Landmark, "x">[], width: number): Landmark[] {
	// Evenly space the level's landmarks across the middle 80% of the world.
	const n = base.length;
	return base.map((lm, i) => ({
		...lm,
		x: Math.round(width * (0.12 + (0.76 * (i + 0.5)) / n)),
	}));
}

export const LEVELS: LevelConfig[] = LEVEL_SEEDS.map((seed, i) => {
	const id = i + 1;
	const groundY = GROUND_Y;
	const layout = generateLayout({ seed: 1000 + id, width: seed.width, groundY, difficulty: seed.difficulty });
	return {
		id,
		slug: seed.slug,
		name: seed.name,
		blurb: seed.blurb,
		width: seed.width,
		height: WORLD_HEIGHT,
		groundY,
		theme: seed.theme,
		landmarks: spreadLandmarks(seed.landmarks, seed.width),
		...layout,
	};
});

export const LEVEL_COUNT = LEVELS.length;

export function getLevel(id: number): LevelConfig {
	return LEVELS[Math.max(0, Math.min(LEVELS.length - 1, Math.round(id) - 1))];
}

// Back-compat singleton: level 1. Used by the DO's default room and by tests.
export const LEVEL = LEVELS[0];
